import type { TranslationTree } from '../../types';

export const labeling: TranslationTree = {
  labeling: {
    page: {
      breadcrumb: 'Labeling',
      title: 'Tool-state labeling',
      subtitle:
        'Classify each edge image by tool state to train the predictive-maintenance model. Use the keyboard to go fast.',
    },
    shortcuts: {
      button: 'Shortcuts',
      title: 'Keyboard shortcuts',
      labelOk: 'Label Good (OK)',
      labelWorn: 'Label Worn',
      classify: 'Classify with AI',
      prevNext: 'Previous / next image',
      undo: 'Undo last label',
    },
    toolbar: {
      modeFocus: 'Focus',
      modeGrid: 'Grid',
      modeDichotomous: 'Dichotomous',
      statusAll: 'All',
      statusTodo: 'To do',
      statusDone: 'Done',
      progress: '{{done}} / {{total}} labeled',
      autoAdvance: 'Auto-advance',
      modeLabel: 'Labeling mode',
      statusLabel: 'Status filter',
    },
    state: {
      ok: 'Good (OK)',
      worn: 'Worn',
      shortOk: 'OK',
      shortWorn: 'Worn',
      unreadable: 'Unreadable image',
      shortUnreadable: 'Unreadable',
    },
    panel: {
      stateHead: 'Tool state',
      prev: 'Previous',
      next: 'Next',
      undo: 'Undo',
    },
    cnn: {
      button: 'Classify with AI',
      loading: 'Classifying…',
      suggestion: 'AI suggestion: {{state}}',
      suggestionConfidence: 'AI suggestion: {{state}} ({{confidence}}%)',
      accept: 'Accept',
      reject: 'Reject',
      acceptAll: 'Accept all AI',
      rejectAll: 'Reject all',
      classifyAll: 'All',
      pendingCount: '{{count}} pending',
      noScore: 'no confidence score',
      tag: 'AI',
      error: 'Classification failed',
    },
    dev: {
      resetButton: 'DEV Reset',
      resetTitle: 'Reset annotations',
      resetBody: 'Clears all labels and AI suggestions for the current session. Development only.',
      resetConfirm: 'Reset',
      resetCancel: 'Cancel',
    },
    viewer: {
      imageAlt: 'Tool inspection image',
      zoomFit: 'Fit',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      capturedAt: 'Captured {{datetime}}',
      imageCount: 'Image {{n}} of {{total}}',
    },
    grid: {
      selectedCount: '{{count}} selected',
      markAs: 'Mark as:',
      deselect: 'Deselect',
      bulkClassify: 'Classify with AI',
      selectImage: 'Select image',
      openImage: 'Open in Focus mode',
    },
    legend: {
      ok: 'Good',
      worn: 'Worn',
      unreadable: 'Unreadable',
    },
    complete: {
      banner: 'All images in this filter are labeled. Nice work!',
    },
    selector: {
      title: 'Select a tool to label',
      subtitle: 'Pick a tool to start or continue its labeling session.',
      searchPlaceholder: 'Search by tool or reference',
      filterAll: 'All',
      filterInProgress: 'In progress',
      filterNotStarted: 'Not started',
      statusNotStarted: 'Not started',
      statusInProgress: 'In progress',
      statusComplete: 'Complete',
      traceCount: '{{count}} traces',
      labeledCount: '{{done}} / {{total}} labeled',
      noReference: 'No reference',
      startLabeling: 'Start labeling',
      cancelSelection: 'Cancel',
      selectedTool: 'Selected: {{tool}}',
      changeTool: 'Change tool',
      emptyTitle: 'No tools available',
      emptyHint: 'No tools were found for your account.',
      errorTitle: 'Could not load tools',
      errorRetry: 'Retry',
      loading: 'Loading tools…',
      experiments: 'Experiments',
      toolsIn: 'Parts in',
      partCount: 'parts in this experiment',
      selectExperimentHint: 'Select an experiment to see its tools.',
    },
    export: {
      button: 'Export (Excel)',
      hint: 'Export the data labeled by every annotator: for each labeled tool, the last good state and the first worn state (part and timestamp).',
      inProgress: 'Exporting… {{done}} / {{total}}',
      empty: 'No labeled tool to export.',
      error: 'Export failed. Please try again.',
    },
    partPicker: {
      title: 'Choose a part',
      hint: 'Pick the image sequence you want to work on.',
      partLabel: 'Part {{partId}}',
      imageCount: '{{count}} images',
      fullyLabeled: 'Already labeled',
      empty: 'No part available for this session.',
      allParts: 'All parts',
      allPartsHint: 'Every part, mixed chronological order.',
      triggerAll: 'All parts',
      triggerSelected: 'Part {{partId}}',
    },
    dichotomous: {
      compare: {
        title: 'Comparison',
        progress: 'Comparison {{n}} of ~{{total}}',
        question: 'Is this image still OK, or already worn?',
        btnOk: 'Still OK',
        btnWorn: 'Worn',
        btnUnreadable: 'Unreadable image',
        rangeLabel: 'Search progress across the sequence',
        endpointFirst: "Confirm the first image: it's expected to be OK.",
        endpointLast: "Confirm the last image: it's expected to be worn.",
      },
      verify: {
        title: 'Verification',
        progress: 'Verification {{n}} of {{total}}',
        question: 'Is this image still OK, or already worn?',
        intro: 'Breakpoint found. Label a few more images to confirm it.',
      },
      conflict: {
        title: 'Unexpected result',
        body: 'Image {{position}} was expected to be "{{expected}}", but you labeled it "{{given}}".',
        expected: 'Expected',
        given: 'Your answer',
        position: 'Image {{index}} of {{total}}',
        actionRerun: 'Re-run search from here',
        actionKeep: 'Keep this breakpoint',
        overwriteWarning:
          "Keeping this breakpoint replaces the label you just gave with the group's label when applied.",
      },
      summary: {
        title: 'Ready to apply',
        counts: '{{ok}} images will be labeled OK, {{worn}} will be labeled Worn.',
        confirm: 'Apply labels',
        cancel: 'Cancel',
        overwriteWarning: 'Existing labels on these images will be replaced.',
        unreadableCount: '{{count}} unreadable image(s) will be labeled Unreadable.',
        restart: 'Restart the search',
      },
      degenerate: {
        allWornTitle: 'No OK images found',
        allWornBody:
          'The first image is already worn: this part shows no OK images in this series.',
        allOkTitle: 'No worn images found',
        allOkBody: 'The last image is still OK: this part shows no wear in this series.',
        allUnreadableTitle: 'No readable image',
        allUnreadableBody:
          'Every image of this series is unreadable: there is no breakpoint to search for.',
        applyAll: 'Label the whole series',
        fallbackManual: 'Label manually',
      },
      done: {
        title: 'Done',
        backToModes: 'Back to labeling modes',
      },
    },
  },
};
